import { useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../api/client'
import type { AccessSystem, TlsCertificate } from '../../api/types'
import { shortDateTime } from '../../lib/format'
import { certExpiry, certHostMismatch, formatFingerprint, shortFingerprint } from '../../lib/tls'
import { useAuthStore } from '../../stores/auth'
import { Button, Card, Chip, ConfirmDeleteModal } from '../ui'

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

function day(iso: string): string {
  return iso ? shortDateTime(iso).slice(0, 10) : '—'
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">{label}</span>
      <span className="break-all text-xs text-ostrel-ink">{children}</span>
    </div>
  )
}

function ExpiryWarning({ notAfter, what }: { notAfter: string; what: string }) {
  const state = certExpiry(notAfter)
  if (state === 'expired')
    return (
      <p className="text-[11px] leading-relaxed text-ostrel-warn">
        {what} has expired ({day(notAfter)}). A pin keeps trusting it; renew the certificate on the
        device, then fetch and re-pin.
      </p>
    )
  if (state === 'soon')
    return (
      <p className="text-[11px] leading-relaxed text-ostrel-warn">
        {what} expires within 30 days ({day(notAfter)}). When the device rolls over to a new
        certificate, connections fail until it is re-pinned.
      </p>
    )
  return null
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      variant="ghost"
      size="sm"
      type="button"
      onClick={() => {
        navigator.clipboard
          ?.writeText(text)
          .then(() => setCopied(true))
          .catch(() => {
            /* clipboard unavailable — the fingerprint stays selectable */
          })
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  )
}

/**
 * TLS certificate pinning for a system with a `config.base_url`
 * (POST /systems/{uid}/tls/fetch, POST|DELETE /systems/{uid}/tls/pin).
 * Trust-on-first-use: the admin fetches what the device presents, compares
 * the full SHA-256 against the fingerprint the device itself shows, and
 * pins. The server re-fetches on pin and refuses (409) if the certificate
 * changed in between. Actions are admin-only (GatewayDetail pattern).
 */
export function TlsPinCard({ system }: { system: AccessSystem }) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const queryClient = useQueryClient()
  const [fetched, setFetched] = useState<TlsCertificate | null>(null)
  const [confirmUnpin, setConfirmUnpin] = useState(false)

  const baseUrl = typeof system.config.base_url === 'string' ? system.config.base_url : ''
  const pin = system.tls_pin ?? null
  const pinValid = !!pin && /^[0-9a-f]{64}$/i.test(pin.sha256.replace(/:/g, ''))
  const mismatch = pin && pinValid && baseUrl ? certHostMismatch(pin.subject, baseUrl) : null

  const fetchCert = useMutation({
    mutationFn: () => api.fetchSystemCertificate(system.unique_id),
    onMutate: () => setFetched(null),
    onSuccess: (cert) => setFetched(cert),
  })
  const pinCert = useMutation({
    mutationFn: (sha256: string) => api.pinSystemCertificate(system.unique_id, sha256),
    onSuccess: () => {
      setFetched(null)
      queryClient.invalidateQueries({ queryKey: [scope] })
    },
  })
  const unpin = useMutation({
    mutationFn: () => api.unpinSystemCertificate(system.unique_id),
    onSuccess: () => {
      setConfirmUnpin(false)
      queryClient.invalidateQueries({ queryKey: [scope] })
    },
  })

  const fetchedIsPinned = !!fetched && pinValid && fetched.sha256 === pin?.sha256.replace(/:/g, '').toLowerCase()

  return (
    <Card title="TLS certificate">
      <div className="flex flex-col gap-4">
        {!pin ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Chip tone="muted" label="Not pinned" />
            </div>
            <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
              Connections to {baseUrl || 'the endpoint'} use the normal certificate checks. Pin the
              device's certificate to trust exactly that one — the way to connect to a self-signed
              panel or WAVE server without turning verification off.
            </p>
          </div>
        ) : !pinValid ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Chip tone="danger" label="Invalid pin — remove and re-pin" />
            </div>
            <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
              The stored pin is malformed, so every connection to this system is refused.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Chip tone="ok" label="Pinned" />
              {pin.self_signed && <Chip tone="muted" label="Self-signed" />}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Subject">{pin.subject || '—'}</Field>
              <Field label="SHA-256">
                <span className="tnum font-mono" title={formatFingerprint(pin.sha256)}>
                  {shortFingerprint(pin.sha256)}
                </span>
              </Field>
              <Field label="Expires">
                <span className="tnum">{day(pin.not_after)}</span>
              </Field>
            </div>
            {(pin.pinned_at || pin.pinned_by) && (
              <p className="tnum text-[11px] text-ostrel-ink-muted">
                Pinned {pin.pinned_at ? shortDateTime(pin.pinned_at) : ''}
                {pin.pinned_by ? ` by ${pin.pinned_by}` : ''}
              </p>
            )}
            <ExpiryWarning notAfter={pin.not_after} what="The pinned certificate" />
            {mismatch && (
              <p className="text-[11px] leading-relaxed text-ostrel-warn">
                The pinned certificate was issued for {mismatch.certHost}, but the endpoint is now{' '}
                {mismatch.urlHost}. If the system moved, fetch and re-pin its certificate.
              </p>
            )}
            <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
              Changing the endpoint URL keeps this pin — connections then fail until the new
              server's certificate is fetched and pinned.
            </p>
          </div>
        )}

        {isAdmin && (
          <div className="flex flex-wrap gap-2 border-t border-ostrel-line pt-3">
            <Button variant="ghost" size="sm" disabled={fetchCert.isPending} onClick={() => fetchCert.mutate()}>
              {fetchCert.isPending ? 'Fetching…' : 'Fetch certificate'}
            </Button>
            {pin && (
              <Button variant="danger" size="sm" onClick={() => setConfirmUnpin(true)}>
                Remove pin
              </Button>
            )}
          </div>
        )}

        {fetchCert.error && (
          <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
            {errDetail(fetchCert.error)}
          </p>
        )}

        {isAdmin && fetched && (
          <div className="flex flex-col gap-3 rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Presented now
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Subject">{fetched.subject || '—'}</Field>
              <Field label="Issuer">
                {fetched.issuer || '—'}
                {fetched.self_signed && ' (self-signed)'}
              </Field>
              <Field label="Valid">
                <span className="tnum">
                  {day(fetched.not_before)} – {day(fetched.not_after)}
                </span>
              </Field>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                SHA-256 fingerprint
              </span>
              <div className="flex items-start gap-2">
                <code className="tnum min-w-0 flex-1 select-all break-all rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 font-mono text-xs text-ostrel-ink">
                  {formatFingerprint(fetched.sha256)}
                </code>
                <CopyButton text={formatFingerprint(fetched.sha256)} />
              </div>
            </div>
            <p className="text-[11px] leading-relaxed text-ostrel-ink">
              Compare this with the fingerprint shown on the device itself (its admin page or
              console) — not one sent to you by someone else. Pin only if every pair matches: whatever
              certificate you pin is trusted from then on.
            </p>
            <ExpiryWarning notAfter={fetched.not_after} what="This certificate" />
            {fetchedIsPinned ? (
              <p className="text-[11px] text-ostrel-ok">This is the certificate already pinned.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="navy"
                  size="sm"
                  disabled={pinCert.isPending}
                  onClick={() => pinCert.mutate(fetched.sha256)}
                >
                  {pinCert.isPending ? 'Pinning…' : 'Pin this certificate'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setFetched(null)}>
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        )}

        {pinCert.error && (
          <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
            {errDetail(pinCert.error)}
          </p>
        )}
      </div>

      <ConfirmDeleteModal
        open={confirmUnpin}
        title="Remove TLS pin"
        objectName={system.name}
        expected={system.unique_id}
        pending={unpin.isPending}
        error={unpin.error ? errDetail(unpin.error) : null}
        confirmLabel={unpin.isPending ? 'Removing…' : 'Remove pin'}
        onConfirm={() => unpin.mutate()}
        onClose={() => setConfirmUnpin(false)}
      >
        <p>
          Remove the certificate pin from <strong>{system.name}</strong> ({system.unique_id})?
          Connections fall back to the normal certificate checks, which a self-signed device fails.
        </p>
      </ConfirmDeleteModal>
    </Card>
  )
}
